import React from 'react';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import { DataTable, DataTableHeader, DataTableHeadCell, DataTableBody, Badge } from '@/shared/components/ui';
import type { TeamPerformanceResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface TeamPerformanceTableProps {
  data: TeamPerformanceResponse;
}

/**
 * Tabela de desempenho da equipe
 *
 * Segue o padrão visual dos widgets da dashboard principal:
 * - Header compacto com WidgetHeaderIcon
 * - Tipografia consistente (text-sm font-semibold, text-xs)
 * - tabular-nums para valores numéricos
 */
export const TeamPerformanceTable: React.FC<TeamPerformanceTableProps> = ({ data }) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getConversionBadgeVariant = (rate: number): 'success' | 'warning' | 'destructive' => {
    if (rate >= 30) return 'success';
    if (rate >= 15) return 'warning';
    return 'destructive';
  };

  return (
    <div
      className="rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3
              className="text-[15px] font-bold tracking-[-0.01em]"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Desempenho da Equipe
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {data.members.length} membros no período
            </p>
          </div>
          <WidgetHeaderIcon icon={'users'} />
        </div>
      </div>

      {/* Top/Bottom Performers Summary */}
      {(data.topPerformers.length > 0 || data.needsImprovement.length > 0) && (
        <div
          className="grid grid-cols-2 gap-4 px-4 py-3 border-t"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          {/* Top Performers */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Icon name="trending-up" className="h-3 w-3" style={{ color: 'var(--theme-success-color)' }} />
              <span
                className="text-[10px] font-medium uppercase tracking-wide"
                style={{ color: 'var(--theme-success-color)' }}
              >
                Melhores Desempenhos
              </span>
            </div>
            <div className="space-y-2">
              {data.topPerformers.slice(0, 3).map((performer, index) => (
                <div
                  key={performer.userId}
                  className="flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-2">
                    {/* Mockup `.tp-item .rk`: chip NEUTRO cinza 19px (não verde) */}
                    <span
                      className="w-[19px] h-[19px] rounded-full flex items-center justify-center text-[10.5px] font-bold"
                      style={{
                        backgroundColor: 'var(--theme-surface-sunken)',
                        color: 'var(--theme-muted-text)',
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ color: 'var(--theme-card-text)' }}>
                      {performer.userName}
                    </span>
                  </div>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {performer.metrics.conversionRate.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Needs Improvement */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Icon name="trending-down" className="h-3 w-3" style={{ color: 'var(--theme-warning-color)' }} />
              <span
                className="text-[10px] font-medium uppercase tracking-wide"
                style={{ color: 'var(--theme-warning-color)' }}
              >
                Precisam de Atenção
              </span>
            </div>
            <div className="space-y-2">
              {data.needsImprovement.slice(0, 3).map((performer) => (
                <div
                  key={performer.userId}
                  className="flex items-center justify-between text-xs"
                >
                  <span style={{ color: 'var(--theme-card-text)' }}>
                    {performer.userName}
                  </span>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--theme-warning-color)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {performer.metrics.conversionRate.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <DataTable style={{ border: 'none', borderRadius: 0 }}>
        <DataTableHeader>
          <DataTableHeadCell>Membro</DataTableHeadCell>
          <DataTableHeadCell align="center">Leads</DataTableHeadCell>
          <DataTableHeadCell align="center">Ganhos</DataTableHeadCell>
          <DataTableHeadCell align="center">Perdidos</DataTableHeadCell>
          <DataTableHeadCell align="center">Conversão</DataTableHeadCell>
          <DataTableHeadCell align="right">Valor Total</DataTableHeadCell>
          <DataTableHeadCell align="center">Atividades</DataTableHeadCell>
        </DataTableHeader>
        <DataTableBody>
          {data.members.map((member) => (
            <tr
              key={member.userId}
              className="border-t"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <td
                className="px-4 py-3"
                style={{ color: 'var(--theme-card-text)' }}
              >
                <div className="flex flex-col">
                  <span className="text-xs font-medium">{member.userName}</span>
                  {member.teamName && (
                    <span
                      className="text-[10px]"
                      style={{ color: 'var(--theme-muted-text)' }}
                    >
                      {member.teamName}
                    </span>
                  )}
                </div>
              </td>
              <td
                className="px-4 py-3 text-center text-xs"
                style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
              >
                {member.metrics.totalLeads}
              </td>
              <td
                className="px-4 py-3 text-center text-xs"
                style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
              >
                {member.metrics.leadsWon}
              </td>
              <td
                className="px-4 py-3 text-center text-xs"
                style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
              >
                {member.metrics.leadsLost}
              </td>
              <td className="px-4 py-3 text-center">
                <Badge variant={getConversionBadgeVariant(member.metrics.conversionRate)}>
                  {member.metrics.conversionRate.toFixed(0)}%
                </Badge>
              </td>
              <td
                className="px-4 py-3 text-right text-xs font-medium"
                style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCurrency(member.metrics.totalValue)}
              </td>
              <td className="px-4 py-3 text-center">
                <div className="flex items-center justify-center gap-1.5 text-xs">
                  <span style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}>
                    {member.metrics.activitiesCompleted}
                  </span>
                  <span style={{ color: 'var(--theme-muted-text)' }}>/</span>
                  <span
                    style={{
                      color: member.metrics.activitiesOverdue > 0
                        ? 'var(--theme-error-color)'
                        : 'var(--theme-muted-text)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {member.metrics.activitiesOverdue} atrasadas
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </DataTableBody>
      </DataTable>

      {data.members.length === 0 && (
        <div
          className="p-8 text-center text-xs"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Nenhum membro encontrado no período
        </div>
      )}
    </div>
  );
};

TeamPerformanceTable.displayName = 'TeamPerformanceTable';
